import React from "react";
import { func, node, oneOf, oneOfType, string } from "prop-types";

import calloutCardThemes from "../../constants/calloutCardThemes";
import imagePositions from "../../constants/imagePositions";
import * as S from "../styled";

const VerticalCalloutCardLink = React.forwardRef(
  (
    {
      callToActionCopy,
      description,
      headline,
      illustration,
      imagePosition,
      legalCopy,
      theme,
      ...passThroughProps
    },
    ref
  ) => (
    <S.VerticalCardWrapper
      ref={ref}
      as="a"
      interactive
      theme={theme}
      {...passThroughProps}
    >
      {illustration && (
        <S.IllustrationWrapper imagePosition={imagePosition}>
          {illustration}
        </S.IllustrationWrapper>
      )}
      <S.ContentWrapper>
        <S.Headline>{headline}</S.Headline>
        {description && <S.Description>{description}</S.Description>}
      </S.ContentWrapper>
      <S.PseudoLink theme={theme}>{callToActionCopy}</S.PseudoLink>
      {legalCopy && (
        <>
          <S.Separator theme={theme} />
          <S.LegalCopy>{legalCopy}</S.LegalCopy>
        </>
      )}
    </S.VerticalCardWrapper>
  )
);

VerticalCalloutCardLink.propTypes = {
  /**
   * The text inside of the pseudo button within the Callout Card
   */
  callToActionCopy: oneOfType([node, string]).isRequired,

  /**
   * Adds a description block to the Callout Card
   */
  description: string,

  /**
   * Headline for the Callout Card
   */
  headline: string.isRequired,

  /**
   * The URL for the Callout Card.
   * (The entire card is clickable)
   */
  href: string.isRequired,

  /**
   * A component that defines the illustration for the Callout Card.
   */
  illustration: node,

  /**
   * If an illustration is provided, imagePosition will allow to align it to the bottom.
   */
  imagePosition: oneOf([imagePositions.TOP, imagePositions.BOTTOM]),

  /**
   * Adds a legal copy block to the Callout Card
   */
  legalCopy: oneOfType([node, string]),

  /**
   * Click callback handler when a user clicks the Callout Card
   * (the entire card, not just the link)
   */
  onClick: func,

  /**
   * The Callout Card theme. One of:
   *   - BLUE
   *   - ORANGE
   *   - GRAY
   *   - WHITE
   */
  theme: oneOf(Object.values(calloutCardThemes))
};

VerticalCalloutCardLink.defaultProps = {
  description: null,
  legalCopy: null,
  illustration: null,
  imagePosition: imagePositions.TOP,
  onClick: null,
  theme: calloutCardThemes.WHITE
};

VerticalCalloutCardLink.displayName = "VerticalCalloutCardLink";

export default VerticalCalloutCardLink;
